import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, ChevronDown, ChevronRight, Link2, Mail, Pencil, Phone, Plus, Send, StickyNote, Trash2, X } from "lucide-react";
import { platformClient, formatPlatformError } from "./platformApi";

const STATUSES = [
    { id: "lead", label: "Lead" },
    { id: "in_gesprek", label: "In gesprek" },
    { id: "offerte", label: "Offerte verstuurd" },
    { id: "klant", label: "Klant" },
    { id: "verloren", label: "Verloren" },
];

const STATUS_STYLES = {
    lead: "border-white/20 text-ash",
    in_gesprek: "border-sky-400/40 text-sky-300",
    offerte: "border-amber-400/40 text-amber-300",
    klant: "border-gold/60 text-gold",
    verloren: "border-red-400/30 text-red-300/70",
};

const EMPTY_FORM = { company: "", contact_name: "", email: "", phone: "", status: "lead", value: "", next_followup: "", notes: "" };

const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-ash";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);
const isOverdue = (c) =>
    c.next_followup && c.next_followup < todayISO() && c.status !== "klant" && c.status !== "verloren";

export const CrmModule = () => {
    const [contacts, setContacts] = useState([]);
    const [filter, setFilter] = useState("alle");
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [editing, setEditing] = useState(null);
    const [editForm, setEditForm] = useState(EMPTY_FORM);
    const [activityNote, setActivityNote] = useState("");
    const [won, setWon] = useState(null);
    const [portalNotice, setPortalNotice] = useState("");

    const portalLink = async (c) => {
        setPortalNotice("");
        try {
            const { data } = await platformClient.post(`/crm/contacts/${c.id}/portal-link`);
            try {
                await navigator.clipboard.writeText(data.url);
                setPortalNotice(`Portaallink voor ${c.company} gekopieerd`);
            } catch {
                setPortalNotice(`Portaallink: ${data.url}`);
            }
            setTimeout(() => setPortalNotice(""), 6000);
        } catch {}
    };

    const portalEmail = async (c) => {
        setPortalNotice("");
        setError("");
        try {
            const { data } = await platformClient.post(`/crm/contacts/${c.id}/portal-email`);
            setPortalNotice(`Portaallink verstuurd naar ${data.to}`);
            setTimeout(() => setPortalNotice(""), 6000);
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const celebrateWin = (contact, newStatus) => {
        if (newStatus === "klant" && contact.status !== "klant") {
            setWon(contact.id);
            setTimeout(() => setWon(null), 2600);
        }
        patch(contact, { status: newStatus });
    };

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/crm/contacts");
            setContacts(data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const counts = useMemo(() => {
        const c = { alle: contacts.length };
        for (const s of STATUSES) c[s.id] = contacts.filter((x) => x.status === s.id).length;
        return c;
    }, [contacts]);

    const pipelineValue = useMemo(
        () =>
            contacts
                .filter((c) => c.status !== "verloren" && c.status !== "klant")
                .reduce((sum, c) => sum + (c.value || 0), 0),
        [contacts]
    );

    const visible = filter === "alle" ? contacts : contacts.filter((c) => c.status === filter);

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await platformClient.post("/crm/contacts", { ...form, value: Number(form.value) || 0 });
            setForm(EMPTY_FORM);
            setShowForm(false);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const patch = async (contact, update) => {
        setContacts((list) => list.map((c) => (c.id === contact.id ? { ...c, ...update } : c)));
        try {
            await platformClient.patch(`/crm/contacts/${contact.id}`, update);
        } catch {
            load();
        }
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await patch(editing, { ...editForm, value: Number(editForm.value) || 0 });
            setEditing(null);
        } finally {
            setBusy(false);
        }
    };

    const remove = async (contact) => {
        if (!window.confirm(`${contact.company} definitief verwijderen?`)) return;
        setContacts((list) => list.filter((c) => c.id !== contact.id));
        try {
            await platformClient.delete(`/crm/contacts/${contact.id}`);
        } catch {
            load();
        }
    };

    const addActivity = (contact) => {
        if (!activityNote.trim()) return;
        const activities = [
            ...(contact.activities || []),
            { date: todayISO(), note: activityNote.trim() },
        ];
        patch(contact, { activities });
        setActivityNote("");
    };

    const startEdit = (c) => {
        setEditing(c.id);
        setEditForm({
            company: c.company || "",
            contact_name: c.contact_name || "",
            email: c.email || "",
            phone: c.phone || "",
            status: c.status || "lead",
            value: c.value ?? "",
            next_followup: c.next_followup || "",
            notes: c.notes || "",
        });
    };

    const statusLabel = (id) => STATUSES.find((s) => s.id === id)?.label || id;

    const ContactForm = ({ values, setValues, onSubmit, submitLabel, busy, error }) => (
        <form onSubmit={onSubmit} className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
                <label className={labelCls} htmlFor="crm-company">Bedrijf *</label>
                <input id="crm-company" data-testid="crm-form-company" required className={inputCls} value={values.company}
                    onChange={(e) => setValues({ ...values, company: e.target.value })} placeholder="Bedrijfsnaam" />
            </div>
            <div>
                <label className={labelCls} htmlFor="crm-contact">Contactpersoon</label>
                <input id="crm-contact" data-testid="crm-form-contact-name" className={inputCls} value={values.contact_name}
                    onChange={(e) => setValues({ ...values, contact_name: e.target.value })} placeholder="Naam contactpersoon" />
            </div>
            <div>
                <label className={labelCls} htmlFor="crm-email">E-mail</label>
                <input id="crm-email" data-testid="crm-form-email" type="email" className={inputCls} value={values.email}
                    onChange={(e) => setValues({ ...values, email: e.target.value })} placeholder="naam@bedrijf.nl" />
            </div>
            <div>
                <label className={labelCls} htmlFor="crm-phone">Telefoon</label>
                <input id="crm-phone" data-testid="crm-form-phone" className={inputCls} value={values.phone}
                    onChange={(e) => setValues({ ...values, phone: e.target.value })} placeholder="06-12345678" />
            </div>
            <div>
                <label className={labelCls} htmlFor="crm-status">Status</label>
                <select id="crm-status" data-testid="crm-form-status" className={`${inputCls} bg-ink`} value={values.status}
                    onChange={(e) => setValues({ ...values, status: e.target.value })}>
                    {STATUSES.map((s) => (
                        <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className={labelCls} htmlFor="crm-value">Geschatte waarde (€)</label>
                <input id="crm-value" data-testid="crm-form-value" type="number" min="0" step="any" className={inputCls} value={values.value}
                    onChange={(e) => setValues({ ...values, value: e.target.value })} placeholder="0" />
            </div>
            <div>
                <label className={labelCls} htmlFor="crm-followup">Volgende opvolging</label>
                <input id="crm-followup" data-testid="crm-form-next-followup" type="date" className={inputCls} value={values.next_followup}
                    onChange={(e) => setValues({ ...values, next_followup: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="crm-notes">Notities</label>
                <textarea id="crm-notes" data-testid="crm-form-notes" rows={3} className={`${inputCls} resize-none`} value={values.notes}
                    onChange={(e) => setValues({ ...values, notes: e.target.value })} placeholder="Context, afspraken, bijzonderheden…" />
            </div>
            {error ? <p data-testid="crm-form-error" className="sm:col-span-2 text-xs text-red-400">{error}</p> : null}
            <div className="sm:col-span-2">
                <button data-testid="crm-form-submit" disabled={busy}
                    className="border border-gold bg-gold px-8 py-3 text-xs uppercase tracking-[0.25em] text-black transition-colors hover:bg-transparent hover:text-gold disabled:opacity-50">
                    {busy ? "Bezig…" : submitLabel}
                </button>
            </div>
        </form>
    );

    return (
        <div data-testid="crm-module" className="mt-10">
            <div className="flex flex-wrap items-end justify-between gap-6">
                <div>
                    <p className="text-[11px] uppercase tracking-[0.35em] text-gold">CRM</p>
                    <h2 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
                        Klanten &amp; <span className="italic text-gold">leads</span>
                    </h2>
                    <p data-testid="crm-pipeline-value" className="mt-3 text-sm text-ash">
                        Pipeline: <span className="text-gold">{euro(pipelineValue)}</span> in actieve trajecten
                    </p>
                </div>
                <button data-testid="crm-add-button" onClick={() => setShowForm((v) => !v)}
                    className="flex items-center gap-2 border border-gold/50 px-5 py-3 text-xs uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-black">
                    {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    {showForm ? "Sluiten" : "Nieuw contact"}
                </button>
            </div>

            {showForm ? (
                <div data-testid="crm-create-panel" className="mt-8 border border-white/10 bg-coal p-8">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-gold">Nieuw contact</p>
                    <ContactForm values={form} setValues={setForm} onSubmit={create} submitLabel="Toevoegen" busy={busy} error={error} />
                </div>
            ) : null}

            <div data-testid="crm-status-filters" className="mt-10 flex flex-wrap gap-2">
                {[{ id: "alle", label: "Alle" }, ...STATUSES].map((s) => (
                    <button key={s.id} data-testid={`crm-filter-${s.id}`} onClick={() => setFilter(s.id)}
                        className={`border px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors ${
                            filter === s.id
                                ? "border-gold bg-gold/10 text-gold"
                                : "border-white/10 text-ash hover:border-gold/40 hover:text-bone"
                        }`}>
                        {s.label} ({counts[s.id] ?? 0})
                    </button>
                ))}
            </div>

            {portalNotice ? (
                <p data-testid="crm-portal-notice" className="mt-4 border border-gold/30 bg-gold/5 px-4 py-3 text-xs uppercase tracking-[0.15em] text-gold">
                    {portalNotice}
                </p>
            ) : null}
            {error && !showForm && !editing ? (
                <p data-testid="crm-error" className="mt-4 border border-red-400/30 bg-red-400/5 px-4 py-3 text-xs text-red-300">
                    {error}
                </p>
            ) : null}

            <div className="mt-6 space-y-3">
                {visible.length === 0 ? (
                    <p data-testid="crm-empty" className="border border-white/10 bg-coal p-10 text-sm text-ash">
                        Nog geen contacten in dit overzicht.
                    </p>
                ) : (
                    visible.map((c) => {
                        const open = expanded === c.id;
                        const overdue = isOverdue(c);
                        return (
                            <div key={c.id} data-testid={`crm-contact-${c.id}`} className="relative overflow-hidden border border-white/10 bg-coal">
                                {won === c.id ? (
                                    <div data-testid={`crm-won-${c.id}`} className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
                                        <div className="absolute inset-0 bg-gold/15 [animation:wonGlow_2.4s_ease-out_forwards]" />
                                        <p className="relative font-display text-2xl italic tracking-tight text-gold [animation:wonText_2.4s_ease-out_forwards]">
                                            Nieuwe klant binnen
                                        </p>
                                    </div>
                                ) : null}
                                <button data-testid={`crm-contact-toggle-${c.id}`} onClick={() => setExpanded(open ? null : c.id)}
                                    className="flex w-full flex-wrap items-center gap-4 p-5 text-left">
                                    {open ? <ChevronDown className="h-4 w-4 text-gold" /> : <ChevronRight className="h-4 w-4 text-ash" />}
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate font-display text-lg tracking-tight">{c.company}</p>
                                        <p className="text-xs text-ash">{c.contact_name || "—"}</p>
                                    </div>
                                    {overdue ? (
                                        <span data-testid={`crm-overdue-${c.id}`} className="flex items-center gap-1.5 border border-red-400/40 px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-red-300">
                                            <CalendarClock className="h-3 w-3" /> Opvolging verlopen
                                        </span>
                                    ) : c.next_followup ? (
                                        <span className="text-[10px] uppercase tracking-[0.15em] text-ash">
                                            Vervolg: {c.next_followup}
                                        </span>
                                    ) : null}
                                    <span className={`border px-3 py-1 text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLES[c.status] || STATUS_STYLES.lead}`}>
                                        {statusLabel(c.status)}
                                    </span>
                                    {c.value ? <span className="text-sm text-gold">{euro(c.value)}</span> : null}
                                </button>

                                {open ? (
                                    <div className="border-t border-white/10 p-6">
                                        {editing === c.id ? (
                                            <ContactForm values={editForm} setValues={setEditForm} onSubmit={saveEdit}
                                                submitLabel="Opslaan" busy={busy} error={error} />
                                        ) : (
                                            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                                                <div className="space-y-3 text-sm">
                                                    {c.email ? (
                                                        <p className="flex items-center gap-2 text-ash">
                                                            <Mail className="h-3.5 w-3.5 text-gold" />
                                                            <a className="transition-colors hover:text-gold" href={`mailto:${c.email}`}>{c.email}</a>
                                                        </p>
                                                    ) : null}
                                                    {c.phone ? (
                                                        <p className="flex items-center gap-2 text-ash">
                                                            <Phone className="h-3.5 w-3.5 text-gold" />
                                                            <a className="transition-colors hover:text-gold" href={`tel:${c.phone}`}>{c.phone}</a>
                                                        </p>
                                                    ) : null}
                                                    {c.notes ? (
                                                        <p className="flex items-start gap-2 leading-relaxed text-ash">
                                                            <StickyNote className="mt-1 h-3.5 w-3.5 shrink-0 text-gold" /> {c.notes}
                                                        </p>
                                                    ) : null}
                                                    <div className="flex flex-wrap gap-2 pt-2">
                                                        {STATUSES.map((s) => (
                                                            <button key={s.id} data-testid={`crm-set-status-${c.id}-${s.id}`}
                                                                onClick={() => celebrateWin(c, s.id)}
                                                                className={`border px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] transition-colors ${
                                                                    c.status === s.id
                                                                        ? "border-gold bg-gold/10 text-gold"
                                                                        : "border-white/10 text-ash hover:border-gold/40"
                                                                }`}>
                                                                {s.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <div className="flex flex-wrap gap-3 pt-3">
                                                        <button data-testid={`crm-portal-link-${c.id}`} onClick={() => portalLink(c)}
                                                            className="flex items-center gap-2 border border-gold/40 bg-gold/5 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-black">
                                                            <Link2 className="h-3 w-3" /> Portaallink
                                                        </button>
                                                        <button data-testid={`crm-portal-email-${c.id}`} onClick={() => portalEmail(c)}
                                                            className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/50 hover:text-gold">
                                                            <Send className="h-3 w-3" /> Mail portaal
                                                        </button>
                                                        <button data-testid={`crm-edit-${c.id}`} onClick={() => startEdit(c)}
                                                            className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/50 hover:text-gold">
                                                            <Pencil className="h-3 w-3" /> Bewerken
                                                        </button>
                                                        <button data-testid={`crm-delete-${c.id}`} onClick={() => remove(c)}
                                                            className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors hover:border-red-400/50 hover:text-red-300">
                                                            <Trash2 className="h-3 w-3" /> Verwijderen
                                                        </button>
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className={labelCls}>Opvolging</p>
                                                    <div className="mt-3 flex gap-2">
                                                        <input data-testid={`crm-activity-input-${c.id}`} className={inputCls} value={activityNote}
                                                            onChange={(e) => setActivityNote(e.target.value)}
                                                            placeholder="Bijv. gebeld, afspraak gemaakt…"
                                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addActivity(c); } }} />
                                                        <button data-testid={`crm-activity-add-${c.id}`} onClick={() => addActivity(c)}
                                                            className="shrink-0 border border-gold/50 px-4 text-gold transition-colors hover:bg-gold hover:text-black">
                                                            <Plus className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                    <ul data-testid={`crm-activities-${c.id}`} className="mt-4 space-y-2">
                                                        {[...(c.activities || [])].reverse().map((a, i) => (
                                                            <li key={i} className="flex gap-3 text-xs text-ash">
                                                                <span className="shrink-0 text-gold/70">{a.date}</span>
                                                                <span className="leading-relaxed">{a.note}</span>
                                                            </li>
                                                        ))}
                                                        {(c.activities || []).length === 0 ? (
                                                            <li className="text-xs text-ash/60">Nog geen opvolgmomenten vastgelegd.</li>
                                                        ) : null}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};
